import BarChart from "@/assets/svg/creator-shield/bar-chart.svg";
import Dart from "@/assets/svg/creator-shield/dart.svg";
import Earth from "@/assets/svg/creator-shield/earth.svg";
import MoneyWings from "@/assets/svg/creator-shield/money-wings.svg";
import Shield from "@/assets/svg/creator-shield/shield.svg";
import Zap from "@/assets/svg/creator-shield/zap.svg";
import { CSPrimaryBtn } from "@/components/design-system/creatorshield/CSButton";
import { ArrowRight, X } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function BuiltForCreators() {
  const location = useLocation();
  const isLivePage = location.pathname.includes("/live");
  const [flippedCards, setFlippedCards] = useState(new Set());
  const headerRef = useScrollAnimation({ threshold: 0.2 });

  const defaultFeatures = [
    {
      icon: Shield,
      title: "Automatic Protection",
      description: "Zero effort. Maximum coverage. We've got your back.",
      backTitle: "Automatic Protection",
      backDescription:
        "Our dedicated team of 75+ specialists works around the clock to protect every piece of content you create.",
    },
    {
      icon: Dart,
      title: "Smart Detection",
      description: "Advanced AI catches even the smallest clips.",
      backTitle: "Smart Detection",
      backDescription:
        "Advanced algorithms detect even 0.5-second clips across platforms. Nothing escapes our protection system.",
    },
    {
      icon: MoneyWings,
      title: "Recurring Income",
      description: "Turn stolen content into steady revenue streams.",
      backTitle: "Recurring Income",
      backDescription:
        "When others use your content, they earn ad revenue. We make sure that money goes to you instead.",
    },
    {
      icon: BarChart,
      title: "Real-Time Dashboard",
      description: "Complete visibility into your protected content.",
      backTitle: "Real-Time Dashboard",
      backDescription:
        "Track everything in one beautiful interface. Know exactly what's protected and what's earning.",
    },
    {
      icon: Earth,
      title: "Global Coverage",
      description: "Protection across every major platform worldwide.",
      backTitle: "Global Coverage",
      backDescription:
        "We monitor and protect your content across all major platforms in every region worldwide.",
    },
    {
      icon: Zap,
      title: "Instant Setup",
      description: "Get protected in under 60 seconds. Seriously.",
      backTitle: "Instant Setup",
      backDescription:
        "No complicated forms, no credit cards, no hassle. Just simple, instant protection for your content.",
    },
  ];

  const liveFeatures = [
    {
      icon: Shield,
      title: "100% Done For You",
      description:
        "Our team of 75+ specialists works behind the scenes automatically",
      backTitle: "Automatic Protection",
      backDescription:
        "Our dedicated team of 75+ specialists works behind the scenes to protect every stream, clip, highlight, and video automatically. You focus on creating, we handle the protection.",
    },
    {
      icon: MoneyWings,
      title: "Recurring Income",
      description:
        "When someone rips your content, we route that money to you instead.",
      backTitle: "Recurring Income",
      backDescription:
        "Every time someone rips your stream or clip and posts it to YouTube, they earn ad revenue. We make sure that money goes straight to you instead.",
    },
    {
      icon: Zap,
      title: "No Extra Work or Cost",
      description:
        "We absorb all labor costs because we believe in our product.",
      backTitle: "No Extra Work or Cost",
      backDescription:
        "We absorb all the labor costs because we believe in our product and put our money where our mouth is. You pay nothing upfront, ever.",
    },
  ];

  const features = isLivePage ? liveFeatures : defaultFeatures;
  const accentColor = isLivePage ? "#9146FF" : "#FF6B35";
  const headerText = isLivePage
    ? "CREATORSHIELD LIVE BENEFITS"
    : "WHY CREATORSHIELD";
  const titleText = isLivePage ? (
    <>
      Why Streamers <span style={{ color: accentColor }}>Love Us</span>
    </>
  ) : (
    <>
      <span style={{ color: accentColor }}>Built for Creators</span>, By
      Creators
    </>
  );

  return (
    <div
      className={`bg-white pt-20 px-6 ${isLivePage ? "pb-[170px]" : "pb-4"}`}
    >
      <div className="lg:max-w-[90%] 4xl:max-w-[100rem] mx-auto">
        <div ref={headerRef} className="text-center mb-16 animate-fade-rise">
          <p
            className="font-semibold text-xl uppercase tracking-wide mb-4"
            style={{ color: accentColor }}
          >
            {headerText}
          </p>
          <h2 className="text-5xl lg:text-6xl font-semibold text-black mb-6 leading-tight">
            {titleText}
          </h2>
          <p className="text-base text-black mx-auto font-normal">
            Everything you need to protect your content and maximize your
            revenue, all completely free.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const IconSrc = feature.icon;
            const isFlipped = flippedCards.has(index);

            const handleFlip = () => {
              setFlippedCards((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(index)) {
                  newSet.delete(index);
                } else {
                  newSet.add(index);
                }
                return newSet;
              });
            };

            return (
              <div
                key={index} 
                className="relative h-[480px] lg:h-[400px] perspective-1000"
                style={{ perspective: "1000px" }}
              >
                <div
                  className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${
                    isFlipped ? "rotate-y-180" : ""
                  }`}
                  style={{
                    transformStyle: "preserve-3d",
                    transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                  }}
                >
                  {/* Front Side */}
                  <div
                    className={`absolute inset-0 bg-white border border-[#ddd] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(144,146,148,0.05)] flex flex-col overflow-hidden backface-hidden ${
                      isFlipped ? "pointer-events-none" : ""
                    }`}
                    style={{
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                    }}
                  >
                    {/* Icon */}
                    <div className="absolute left-[39px] top-[48px] w-[70px] h-[70px]">
                      <img src={IconSrc} alt="" className="w-full h-full" />
                    </div>

                    {/* Content */}
                    <div className="flex flex-col flex-1 justify-end p-[39px] pt-0 gap-4">
                      <h3 className="text-2xl font-bold text-black leading-tight">
                        {feature.title}
                      </h3>
                      <p className="text-lg text-black max-w-xs font-normal">
                        {feature.description}
                      </p>

                      {/* Button */}
                      <CSPrimaryBtn
                        variant={isLivePage ? "live" : undefined}
                        className=" cursor-pointer"
                        onClick={handleFlip}
                      >
                        Learn more
                        <ArrowRight size={24} />
                      </CSPrimaryBtn>
                    </div>
                  </div>

                  {/* Back Side */}
                  <div
                    className={`absolute inset-0 border border-[#ddd] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(144,146,148,0.05)] flex flex-col overflow-hidden backface-hidden rotate-y-180 ${
                      !isFlipped ? "pointer-events-none" : ""
                    }`}
                    style={{
                      backgroundColor: accentColor,
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    {/* Close Button */}
                    <button
                      onClick={handleFlip}
                      className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white border border-[#ddd] flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
                    >
                      <X size={18} className="text-black" />
                    </button>

                    {/* Icon */}
                    <div className="absolute left-[39px] top-[48px] w-[70px] h-[70px]">
                      <img src={IconSrc} alt="" className="w-full h-full" />
                    </div>

                    {/* Back Content */}
                    <div className="flex flex-col flex-1 justify-center p-[39px] gap-4 mt-24">
                      <h3 className="text-2xl font-bold text-white leading-tight">
                        {feature.backTitle || feature.title}
                      </h3>
                      <p className="text-lg text-white font-normal">
                        {feature.backDescription || feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
